import React from 'react';
import { AppConfirmDialog } from '../components/AppConfirmDialog';
import { UI_Z_CLASS } from '../utils/uiZIndex';

/**
 * Backdrop handlers that only close when the press *started* on the backdrop. A text selection
 * dragged out of the panel ends on the backdrop too — that must not close the window.
 */
export function useBackdropClose(onClose: () => void) {
  const pressRef = React.useRef(false);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  return React.useMemo(
    () => ({
      onMouseDown: (e: React.MouseEvent<HTMLElement>) => {
        pressRef.current = e.target === e.currentTarget;
      },
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        if (e.target === e.currentTarget && pressRef.current) onCloseRef.current();
        pressRef.current = false;
      },
    }),
    [],
  );
}

/**
 * True once `snapshot` differs from its value when `resetKey` last changed. Pass a key that
 * changes when the form opens, switches record or finishes prefilling, so automatic values
 * (prefill, defaults) are part of the baseline and do not count as user input.
 */
export function useDirtySnapshot(snapshot: string, resetKey: unknown): boolean {
  const baselineRef = React.useRef({ key: resetKey, snapshot });
  if (!Object.is(baselineRef.current.key, resetKey)) baselineRef.current = { key: resetKey, snapshot };
  return snapshot !== baselineRef.current.snapshot;
}

/**
 * For forms that prefill themselves asynchronously (AI suggestion, chosen contact …), where a
 * snapshot baseline is unreliable: dirty once the user typed or changed a field. Spread
 * `editProps` on the panel — input and change events bubble up from every field inside.
 */
export function useUserEdited() {
  const [edited, setEdited] = React.useState(false);
  const editProps = React.useMemo(() => {
    const markEdited = () => setEdited(true);
    return { onInput: markEdited, onChange: markEdited };
  }, []);
  return { edited, editProps };
}

type DiscardGuardOptions = {
  /** Unsaved user input present? */
  dirty: boolean;
  onClose: () => void;
  /** While saving: closing is ignored. */
  disabled?: boolean;
  title?: string;
  description: string;
  overlayClassName?: string;
};

/**
 * „Entwurf verwerfen?“ before a form window closes with unsaved input. Route backdrop, Esc and ✕
 * through `requestClose`; an explicit „Abbrechen“ may keep calling onClose directly.
 * Calling `requestClose` while the question is open dismisses it (Esc = weiter bearbeiten).
 */
export function useDiscardGuard({
  dirty,
  onClose,
  disabled = false,
  title = 'Entwurf verwerfen?',
  description,
  overlayClassName = UI_Z_CLASS.saveConfirm,
}: DiscardGuardOptions) {
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const stateRef = React.useRef({ dirty, onClose, disabled, confirmOpen });
  stateRef.current = { dirty, onClose, disabled, confirmOpen };

  const requestClose = React.useCallback(() => {
    const s = stateRef.current;
    if (s.confirmOpen) {
      setConfirmOpen(false);
      return;
    }
    if (s.disabled) return;
    if (s.dirty) setConfirmOpen(true);
    else s.onClose();
  }, []);

  /** Close the question without closing the form (e.g. when the form switches record). */
  const dismiss = React.useCallback(() => setConfirmOpen(false), []);

  const backdropProps = useBackdropClose(requestClose);

  const dialog = (
    <AppConfirmDialog
      open={confirmOpen}
      onClose={() => setConfirmOpen(false)}
      title={title}
      description={description}
      confirmLabel="Verwerfen"
      cancelLabel="Weiter bearbeiten"
      destructive
      overlayClassName={overlayClassName}
      onConfirm={() => {
        setConfirmOpen(false);
        stateRef.current.onClose();
      }}
    />
  );

  return { requestClose, dismiss, backdropProps, confirmOpen, dialog };
}
